import type { ComponentType, ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from '@staff-app/ui';
import type { TestingDeviceCommands } from '../../../hooks/useTestingDeviceCommands';
import type { TestingDeviceDetail } from '../lib/types';

/** Props every phase-3 command dialog takes from the drawer. */
export interface TestingCommandDialogProps {
  open: boolean;
  detail: TestingDeviceDetail;
  deviceName: string;
  commands: TestingDeviceCommands;
  onClose: () => void;
  /** After a successful send: refetch detail, activity, staff actions, command history. */
  onDone: () => void;
}

/** Fixed-height dialog shell (header · scrolling body · footer with Cancel/Close first). */
export function TestingDialogFrame({
  open,
  onClose,
  title,
  icon: Icon,
  testId,
  size = 'md',
  children,
  footer,
  closeLabel = 'Cancel',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  icon: ComponentType<{ className?: string }>;
  testId: string;
  size?: 'md' | 'lg' | 'xl';
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className={cn(
          'flex max-h-[85vh] flex-col overflow-hidden',
          size === 'md' && 'sm:max-w-md',
          size === 'lg' && 'sm:max-w-xl',
          size === 'xl' && 'sm:max-w-3xl',
        )}
        aria-describedby={undefined}
        data-testid={testId}
      >
        <DialogHeader variant="muted" className="mb-0">
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-muted-foreground" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4 scroll-thin">{children}</div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" data-testid={`${testId}-cancel`}>
              {closeLabel}
            </Button>
          </DialogClose>
          {footer}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Second-step confirm for device-changing sends (install, OTA check, Wi-Fi remove, execute). The
 * testing devices are real hardware — nothing destructive goes out on one click.
 */
export function TestingConfirm({
  open,
  title,
  description,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <AlertDialogContent data-testid="testing-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="break-words">{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{'Cancel'}</AlertDialogCancel>
          <Button variant="destructive" onClick={onConfirm} data-testid="testing-confirm-dialog-confirm">
            {confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
